import React from "react";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import Navbar from "../../components/Navbar";

const missionPoints = [
  "Deliver globally recognized ISO-certified consulting solutions",
  "Empower organizations through sustainable and ethical practices",
  "Enhance operational efficiency with data-driven strategies",
  "Build long-term partnerships based on trust and excellence",
];

const Mission = () => {
  return (
    <>
    <Navbar />
    <section className="bg-gray-50 py-16 sm:py-12 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ===== Header ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            Our Mission
          </h2>
          <div className="w-20 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4" />
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            Our mission is to guide organizations towards excellence by
            implementing internationally recognized standards, fostering
            sustainability, and delivering measurable business value.
          </p>
        </motion.div>

        {/* ===== Content Grid ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* ===== Left Content ===== */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Driving Excellence Through Standards
            </h3>

            <p className="text-gray-600 mb-6 leading-relaxed">
              At <span className="font-semibold text-gray-900">MBA Solutions</span>,
              our mission is centered on helping organizations achieve
              compliance, credibility, and continuous improvement. We combine
              industry expertise with practical implementation strategies to
              ensure long-term success.
            </p>

            <div className="space-y-4">
              {missionPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-6 h-6 text-[var(--bg-primary)] mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700 leading-relaxed">
                    {point}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ===== Right Card ===== */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            whileHover={{
              y: -8,
              boxShadow: "0px 25px 50px rgba(59,130,246,0.25)",
            }}
            className="relative bg-white rounded-2xl border border-gray-100 p-8 shadow-lg overflow-hidden group"
          >
            {/* Glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <h4 className="relative z-10 text-xl font-bold text-gray-900 mb-4">
              Our Commitment
            </h4>

            <p className="relative z-10 text-gray-600 leading-relaxed mb-4">
              We are committed to delivering reliable consulting services that
              align with international standards while remaining practical,
              scalable, and results-oriented for businesses of all sizes.
            </p>

            <p className="relative z-10 text-gray-600 leading-relaxed">
              Through innovation, integrity, and continuous improvement, we help
              organizations build resilient systems that stand the test of time.
            </p>
          </motion.div>

        </div>
      </div>
    </section>
    </>
  );
};

export default Mission;
